import { Component, ElementRef, ViewChild, signal, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { QrcodeService } from '@app/common-codes/services/qrcode.service';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { Employee } from '@app/common-codes/models/matwins-tenant/Employee.model';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';

@Register(Employee)
@Component({
    selector: 'app-employee-qrcode',
    standalone: true,
    imports: [TranslatePipe, MultiSelectComponent],
    templateUrl: './employee-qrcode.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: ``
})
export class EmployeeQrCodeComponent extends GenetricDetailComponent<Employee> {

    @ViewChild('qrCodeContainer') qrCodeContainer?: ElementRef<HTMLElement>;

    override ComponentEntityClass = Employee;
    override Skin: IStaticStylesConfig = MatwinsSkins.Ipad1;
    override isModal = true;

    employees = signal<LookupItem[]>([]);
    selectedKeys = signal<string[]>([]);
    generatedQrCode = signal<string | null>(null);
    generatedEmployeeName = signal<string>('');

    constructor(
        route: ActivatedRoute,
        router: Router,
        translationService: TranslationService,
        validationService: GenericValidationService,
        ngZone: NgZone,
        private navService: NavigationService,
        private tenantService: CurrentTenantService,
        private qrCodeService: QrcodeService,
        alertModalService: AlertModalService
    ) {
        super(route, router, translationService, validationService, ngZone);
        this.alertModalService = alertModalService;
    }

    override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        await this.lookupService.getLookupsByProfiles(this.dataService, [
            { lookupName: 'Employee' }
        ]);
        this.employees.set(this.getLookupData('Employee'));
    }

    override getEmptyEntity(): Employee {
        const employee = new Employee();
        employee.status = 1;
        return employee;
    }

    override parseToEntityInstance(): void {
        if (this.EntityInstance && this.editingEntity()) {
            Object.assign(this.EntityInstance, this.editingEntity());
        }
    }

    onEmployeeSelectChange(keys: string[]): void {
        this.selectedKeys.set(keys);
        this.generatedQrCode.set(null);
    }

    async generate(): Promise<void> {
        const id = this.selectedKeys()[0];
        if (!id) return;

        this.loaderService.show();
        try {
            const result = await this.dataService.execute<{ qrCode: string }>(
                'POST', 'generateQrCode', { employeeId: Number(id) }
            );

            if (!result?.qrCode) {
                this.alertModalService?.showToast('Erreur lors de la génération du QR code', ToastType.Error, 3000);
                return;
            }

            this.generatedQrCode.set(result.qrCode);
            this.generatedEmployeeName.set(
                this.employees().find(e => e.id === id)?.value ?? ''
            );

            // Rendre le QR dans le DOM puis imprimer
            setTimeout(() => this.renderAndPrint(result.qrCode));
        } finally {
            this.loaderService.hide();
        }
    }

    private renderAndPrint(qrCode: string): void {
        const container = this.qrCodeContainer?.nativeElement;
        if (container) {
            container.innerHTML = '';
            this.qrCodeService.appendToElement(container, qrCode, { width: 220, height: 220 });
        }
        this.printQrCode(qrCode);
    }

    private async printQrCode(qrCode: string): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        const windowAny = window as any;
        const hasNativePrinter = windowAny.webkit?.messageHandlers?.MatWins && pos?.urlAddressPrinter;

        const blob = await this.qrCodeService.getPngBlob(qrCode, { width: 400, height: 400 });

        if (hasNativePrinter) {
            const base64 = await this.blobToBase64(blob);
            const obj = {
                function: 'print',
                args: {
                    url: pos!.urlAddressPrinter,
                    fileBase64: base64,
                    printerLabelType: pos!.printerLabelType,
                    printerLabelSize: pos!.printerLabelSize
                }
            };
            windowAny.webkit.messageHandlers.MatWins.postMessage(obj).then((message: any) => {
                if (!message?.isPrinted) {
                    this.alertModalService?.showToast('Erreur impression: ' + message?.error, ToastType.Error, 5000, true);
                }
            });
        } else {
            // Fallback dev : télécharge le PNG (pas d'imprimante native hors WKWebView)
            await this.qrCodeService.download(qrCode, `qrcode-${this.generatedEmployeeName() || 'employee'}`, 'png');
        }
    }

    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    cancel(): void {
        this.goHome();
    }

    goBack(): void {
        this.navService.goBack();
    }

    goHome(): void {
        this.navService.navigateTo('/ipad/home');
    }
}
